import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { companyAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import {
    Video,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Eye,
    EyeOff,
    Download,
    ExternalLink,
    Trash2
} from 'lucide-react';

export default function ZoomSettingsPage() {
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [loading, setLoading] = useState(false);
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [isConfigured, setIsConfigured] = useState(false);
    const [formData, setFormData] = useState({
        client_id: '',
        client_secret: '',
        account_id: ''
    });
    const [message, setMessage] = useState({ type: '', text: '' });

    // Get company_id from API
    const [companyId, setCompanyId] = useState(null);

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const API_BASE = process.env.REACT_APP_BACKEND_URL;
                const authToken = localStorage.getItem('token');
                if (!authToken) return;
                
                const res = await fetch(`${API_BASE}/api/companies/my`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.id) {
                        setCompanyId(data.id);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch company:', error);
            }
        };
        
        if (user) {
            fetchCompany();
        }
    }, [user]);

    useEffect(() => {
        if (companyId) {
            fetchZoomSettings();
        }
    }, [companyId]);

    const fetchZoomSettings = async () => {
        setLoadingSettings(true);
        try {
            const response = await companyAPI.getZoomSettings(companyId);
            if (response.data) {
                setIsConfigured(true);
                setFormData({
                    client_id: response.data.client_id || '',
                    client_secret: '••••••••••••••••',
                    account_id: response.data.account_id || ''
                });
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Failed to load Zoom settings:', error);
            }
            setIsConfigured(false);
        } finally {
            setLoadingSettings(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            await companyAPI.saveZoomSettings(companyId, formData);
            setMessage({
                type: 'success',
                text: 'Zoom settings saved successfully! Zoom is now available for meetings.'
            });
            setIsConfigured(true);
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.detail || 'Failed to save Zoom settings'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete Zoom settings? All workspaces will lose access to Zoom meetings.')) {
            return;
        }

        setDeleting(true);
        try {
            await companyAPI.deleteZoomSettings(companyId);
            setMessage({
                type: 'success',
                text: 'Zoom settings deleted successfully'
            });
            setIsConfigured(false);
            setFormData({
                client_id: '',
                client_secret: '',
                account_id: ''
            });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.detail || 'Failed to delete Zoom settings'
            });
        } finally {
            setDeleting(false);
        }
    };

    const handleDownloadGuide = () => {
        // Create guide content
        const guideContent = `
ZOOM INTEGRATION SETUP GUIDE
============================

Follow these steps to get your Zoom credentials:

1. Go to Zoom Marketplace
   Visit: https://marketplace.zoom.us/

2. Sign in with your Zoom account
   Use your Zoom credentials to log in

3. Create a Server-to-Server OAuth App
   - Click "Develop" → "Build App"
   - Select "Server-to-Server OAuth"
   - Click "Create"

4. Fill in App Information
   - App Name: Your Company Name - Meeting Integration
   - Company Name: Your Company Name
   - Developer Email: Your email address
   - Click "Continue"

5. Get Your Credentials
   After creating the app, you will see:
   - Account ID: Copy this value
   - Client ID: Copy this value
   - Client Secret: Copy this value

6. Configure Scopes
   Add these required scopes:
   - meeting:write:admin (Create meetings)
   - meeting:read:admin (Read meeting details)
   - user:read:admin (Read user information)

7. Activate the App
   - Click "Continue" to complete activation
   - Your app is now ready!

8. Enter Credentials
   - Go to Zoom Settings in your company dashboard
   - Enter the Account ID, Client ID, and Client Secret
   - Click "Save Settings"

9. Done!
   Your company can now use Zoom for meetings.

Important Notes:
- Only company owners can configure Zoom settings
- Keep your credentials secure
- Do not share your Client Secret with anyone

For more information, visit:
https://developers.zoom.us/docs/internal-apps/s2s-oauth/
        `.trim();

        // Create blob and download
        const blob = new Blob([guideContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Zoom_Integration_Setup_Guide.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (!companyId) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto p-6">
                    <Alert>
                        <AlertCircle className="w-4 h-4" />
                        <AlertDescription>
                            Zoom integration is only available for companies. Your workspace is not part of a company.
                        </AlertDescription>
                    </Alert>
                </div>
            </Layout>
        );
    }

    if (loadingSettings) {
        return (
            <Layout>
                <div className="max-w-4xl mx-auto p-6">
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Video className="w-6 h-6 text-blue-500" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-brand-text-primary">Zoom Settings</h1>
                            <p className="text-sm text-brand-text-muted">
                                Configure Zoom integration for your company
                            </p>
                        </div>
                    </div>
                    {isConfigured && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-700">Configured</span>
                        </div>
                    )}
                </div>

                {/* Download Guide */}
                <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <h3 className="font-semibold text-brand-text-primary mb-1">Need help?</h3>
                                <p className="text-sm text-brand-text-muted">
                                    Download our step-by-step guide to get your Zoom credentials from Zoom Marketplace.
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDownloadGuide}
                                className="shrink-0"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Download Guide
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Message */}
                {message.text && (
                    <Alert className={message.type === 'success' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
                        {message.type === 'success' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                            <AlertCircle className="w-4 h-4 text-red-600" />
                        )}
                        <AlertDescription className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                            {message.text}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Settings Form */}
                <Card>
                    <CardHeader>
                        <CardTitle>Zoom Credentials</CardTitle>
                        <CardDescription>
                            Enter your Zoom Server-to-Server OAuth app credentials. These will be encrypted and stored securely.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="account_id">Account ID *</Label>
                                <Input
                                    id="account_id"
                                    name="account_id"
                                    value={formData.account_id}
                                    onChange={handleInputChange}
                                    placeholder="Enter your Zoom Account ID"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="client_id">Client ID *</Label>
                                <Input
                                    id="client_id"
                                    name="client_id"
                                    value={formData.client_id}
                                    onChange={handleInputChange}
                                    placeholder="Enter your Zoom Client ID"
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="client_secret">Client Secret *</Label>
                                <div className="relative">
                                    <Input
                                        id="client_secret"
                                        name="client_secret"
                                        type={showClientSecret ? 'text' : 'password'}
                                        value={formData.client_secret}
                                        onChange={handleInputChange}
                                        placeholder="Enter your Zoom Client Secret"
                                        required={!isConfigured}
                                        className="pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowClientSecret(!showClientSecret)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text-muted hover:text-brand-text-primary"
                                    >
                                        {showClientSecret ? (
                                            <EyeOff className="w-4 h-4" />
                                        ) : (
                                            <Eye className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                                {isConfigured && (
                                    <p className="text-xs text-brand-text-muted">
                                        Leave unchanged to keep existing secret, or enter new secret to update
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => window.open('https://marketplace.zoom.us/', '_blank')}
                                >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Open Zoom Marketplace
                                </Button>

                                <div className="flex items-center gap-3">
                                    {isConfigured && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleDelete}
                                            disabled={deleting}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            {deleting ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4 mr-2" />
                                            )}
                                            Delete Settings
                                        </Button>
                                    )}
                                    <Button type="submit" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                                {isConfigured ? 'Update Settings' : 'Save Settings'}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </CardContent>
                </Card>

                {/* Info Card */}
                <Card className="border-brand-border">
                    <CardHeader>
                        <CardTitle className="text-base">Important Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm text-brand-text-muted">
                        <p>• Only company owners can configure Zoom settings</p>
                        <p>• Zoom meetings will be available for all workspaces in your company</p>
                        <p>• Your credentials are encrypted and stored securely</p>
                        <p>• You need a Zoom account to create a Server-to-Server OAuth app</p>
                        <p>• Required scopes: meeting:write:admin, meeting:read:admin, user:read:admin</p>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
